// Утилиты для оптимизации производительности

// Debounce функция для ограничения частоты вызовов
export const debounce = (func, wait, immediate = false) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      timeout = null
      if (!immediate) func(...args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func(...args)
  }
}

// Throttle функция для ограничения частоты выполнения
export const throttle = (func, limit) => {
  let inThrottle
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

// Мемоизация функций
export const memoize = (fn, getKey = (...args) => JSON.stringify(args)) => {
  const cache = new Map()
  
  return (...args) => {
    const key = getKey(...args)
    
    if (cache.has(key)) {
      return cache.get(key)
    }
    
    const result = fn(...args)
    cache.set(key, result)
    
    // Ограничиваем размер кэша
    if (cache.size > 100) {
      const firstKey = cache.keys().next().value
      cache.delete(firstKey)
    }
    
    return result
  }
}

// Intersection Observer для lazy loading
export const createIntersectionObserver = (callback, options = {}) => {
  const defaultOptions = {
    threshold: 0.1,
    rootMargin: '50px'
  }
  
  return new IntersectionObserver(callback, { ...defaultOptions, ...options })
}

// Проверка поддержки WebP
export const supportsWebP = (() => {
  let supported = null
  
  return () => {
    if (supported !== null) return supported
    
    if (typeof window === 'undefined') return false
    
    const canvas = document.createElement('canvas')
    canvas.width = 1
    canvas.height = 1
    
    supported = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0
    return supported
  }
})()

// Проверка поддержки AVIF
export const supportsAVIF = (() => {
  let supported = null
  
  return () => {
    if (supported !== null) return supported
    
    if (typeof window === 'undefined') return false
    
    const canvas = document.createElement('canvas')
    canvas.width = 1
    canvas.height = 1
    
    supported = canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0
    return supported
  }
})()

// Получение оптимального формата изображения
export const getOptimalImageFormat = (originalSrc, webpSrc, avifSrc) => {
  if (avifSrc && supportsAVIF()) return avifSrc
  if (webpSrc && supportsWebP()) return webpSrc
  return originalSrc
}

// Предзагрузка ресурсов
export const preloadResource = (href, as = 'fetch', crossorigin = 'anonymous') => {
  if (typeof window === 'undefined') return
  
  const link = document.createElement('link')
  link.rel = 'preload'
  link.href = href
  link.as = as
  if (crossorigin) link.crossOrigin = crossorigin
  
  document.head.appendChild(link)
}

// Предзагрузка изображений
export const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

// Батчинг DOM операций
export const batchDOMUpdates = (callback) => {
  if (typeof window === 'undefined') {
    callback()
    return
  }
  
  if ('requestIdleCallback' in window) {
    requestIdleCallback(callback)
  } else {
    requestAnimationFrame(callback)
  }
}

// Измерение производительности
export const measurePerformance = (name, fn) => {
  if (typeof window === 'undefined' || !window.performance) {
    return fn()
  }
  
  const start = performance.now()
  const result = fn()
  const end = performance.now()
  
  console.log(`${name} took ${end - start} milliseconds`)
  
  return result
}

// Кэш для API запросов
class APICache {
  constructor(maxSize = 50, ttl = 5 * 60 * 1000) { // 5 минут TTL
    this.cache = new Map()
    this.maxSize = maxSize
    this.ttl = ttl
  }
  
  set(key, value) {
    // Удаляем старые записи если кэш переполнен
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value
      this.cache.delete(firstKey)
    }
    
    this.cache.set(key, {
      value,
      timestamp: Date.now()
    })
  }
  
  get(key) {
    const item = this.cache.get(key)
    
    if (!item) return null
    
    // Проверяем TTL
    if (Date.now() - item.timestamp > this.ttl) {
      this.cache.delete(key)
      return null
    }
    
    return item.value
  }
  
  clear() {
    this.cache.clear()
  }
  
  delete(key) {
    this.cache.delete(key)
  }
}

export const apiCache = new APICache()

// Оптимизация скролла
export const optimizeScroll = (callback, options = {}) => {
  const { throttleMs = 16, passive = true } = options
  
  const throttledCallback = throttle(callback, throttleMs)
  
  const eventOptions = passive ? { passive: true } : false
  
  window.addEventListener('scroll', throttledCallback, eventOptions)
  
  return () => {
    window.removeEventListener('scroll', throttledCallback, eventOptions)
  }
}

// Оптимизация resize
export const optimizeResize = (callback, options = {}) => {
  const { debounceMs = 250 } = options
  
  const debouncedCallback = debounce(callback, debounceMs)
  
  window.addEventListener('resize', debouncedCallback, { passive: true })
  
  return () => {
    window.removeEventListener('resize', debouncedCallback)
  }
}

// Проверка видимости элемента
export const isElementVisible = (element) => {
  if (!element) return false
  
  const rect = element.getBoundingClientRect()
  const windowHeight = window.innerHeight || document.documentElement.clientHeight
  const windowWidth = window.innerWidth || document.documentElement.clientWidth
  
  return (
    rect.top < windowHeight &&
    rect.bottom > 0 &&
    rect.left < windowWidth &&
    rect.right > 0
  )
}

// Получение метрик производительности
export const getPerformanceMetrics = () => {
  if (typeof window === 'undefined' || !window.performance) {
    return null
  }
  
  const navigation = performance.getEntriesByType('navigation')[0]
  const paint = performance.getEntriesByType('paint')
  
  return {
    // Время загрузки DOM
    domContentLoaded: navigation?.domContentLoadedEventEnd - navigation?.domContentLoadedEventStart,
    
    // Полное время загрузки
    loadComplete: navigation?.loadEventEnd - navigation?.loadEventStart,
    
    // First Paint
    firstPaint: paint.find(entry => entry.name === 'first-paint')?.startTime,
    
    // First Contentful Paint
    firstContentfulPaint: paint.find(entry => entry.name === 'first-contentful-paint')?.startTime,
    
    // Time to Interactive (приблизительно)
    timeToInteractive: navigation?.domInteractive - navigation?.navigationStart
  }
}

// Мониторинг производительности
export const startPerformanceMonitoring = () => {
  if (typeof window === 'undefined') return
  
  // Логируем метрики после загрузки
  window.addEventListener('load', () => {
    setTimeout(() => {
      const metrics = getPerformanceMetrics()
      console.log('Performance Metrics:', metrics)
    }, 0)
  })
  
  // Мониторинг Long Tasks (если поддерживается)
  if ('PerformanceObserver' in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.duration > 50) {
            console.warn('Long Task detected:', entry.duration + 'ms')
          }
        })
      })
      
      observer.observe({ entryTypes: ['longtask'] })
    } catch (e) {
      // PerformanceObserver не поддерживается
    }
  }
}